import React from 'react';
import { View, Text, StyleProp, ViewStyle } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { RADIUS, shadow } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { BadgeTone, toneColors } from './ui/Badge';

interface StatCardProps {
  label: string;
  value: string;
  /** Color family of the icon badge. */
  tone?: BadgeTone;
  icon?: React.ComponentProps<typeof MaterialCommunityIcons>['name'];
  /** Small secondary line under the value. */
  hint?: string;
  /** Horizontal layout (icon left) for narrow screens. */
  compact?: boolean;
  style?: StyleProp<ViewStyle>;
}

/** KPI tile: icon badge, big value, muted label. */
export default function StatCard({ label, value, tone = 'primary', icon, hint, compact, style }: StatCardProps) {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const { fg, bg } = toneColors(colors, tone);

  if (compact) {
    return (
      <View style={[styles.card, styles.compactCard, shadow(colors, 1), style]}>
        {icon ? (
          <View style={[styles.icon, styles.compactIcon, { backgroundColor: bg }]}>
            <MaterialCommunityIcons name={icon} size={20} color={fg} />
          </View>
        ) : null}
        <View style={styles.compactText}>
          <Text style={styles.label} numberOfLines={1}>{label}</Text>
          <Text style={[styles.value, styles.compactValue]} numberOfLines={1}>{value}</Text>
          {hint ? <Text style={styles.hint} numberOfLines={1}>{hint}</Text> : null}
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.card, shadow(colors, 1), style]}>
      {icon ? (
        <View style={[styles.icon, { backgroundColor: bg }]}>
          <MaterialCommunityIcons name={icon} size={20} color={fg} />
        </View>
      ) : null}
      <Text style={styles.label} numberOfLines={1}>{label}</Text>
      <Text style={styles.value} numberOfLines={1} adjustsFontSizeToFit>{value}</Text>
      {hint ? <Text style={styles.hint} numberOfLines={1}>{hint}</Text> : null}
    </View>
  );
}

const useStyles = makeStyles((c) => ({
  card: {
    flex: 1,
    minWidth: 200,
    backgroundColor: c.surface,
    borderRadius: RADIUS.lg,
    borderWidth: 1,
    borderColor: c.divider,
    padding: 20,
  },
  icon: {
    width: 40,
    height: 40,
    borderRadius: RADIUS.md,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  label: { fontSize: 14, fontWeight: '500', color: c.textSecondary },
  value: { fontSize: 26, lineHeight: 32, fontWeight: '800', color: c.textPrimary, marginTop: 4, letterSpacing: -0.4 },
  hint: { fontSize: 12, color: c.textMuted, marginTop: 4 },
  compactCard: { flexDirection: 'row', alignItems: 'center', gap: 14, padding: 16, minWidth: 0 },
  compactIcon: { marginBottom: 0 },
  compactText: { flex: 1, minWidth: 0 },
  compactValue: { fontSize: 20, lineHeight: 26, marginTop: 2 },
}));
